/**
 * DesktopSidebar — linke Desktop-Spalte für 3-Spalten-Layout (≥1440px).
 * Zeigt exklusiv die bereits vorhandenen Profil-Unterseiten,
 * KEINE erfundenen Menüpunkte.
 */
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Home, Bell, MessageSquare, Bookmark, UserPlus, User, ShoppingBag, Sparkles, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ITEMS = [
  { key: "home",           icon: Home,          path: "/",              testId: "sidebar-home",           label: "Start" },
  { key: "marketplace",    icon: ShoppingBag,   path: "/marketplace",   testId: "sidebar-marketplace",    label: "Marketplace" },
  { key: "stilltouchedx",  icon: Sparkles,      path: "/stilltouchedx", testId: "sidebar-stilltouchedx",  label: "stilltouchedX" },
  { key: "notifications",  icon: Bell,          path: "/notifications", testId: "sidebar-notifications",  label: "Benachrichtigungen", watchBadge: true },
  { key: "messages",       icon: MessageSquare, path: "/messages",      testId: "sidebar-messages",       label: "Nachrichten",        unreadBadge: true },
  { key: "bookmarks",      icon: Bookmark,      path: "/bookmarks",     testId: "sidebar-bookmarks",      label: "Sammlungen" },
  { key: "subscriptions",  icon: UserPlus,      path: "/subscriptions", testId: "sidebar-subscriptions",  label: "Abonnements" },
  { key: "studio",         icon: LayoutGrid,    path: "/studio",        testId: "sidebar-studio",         label: "Studio" },
  { key: "profile",        icon: User,          path: "/profile",       testId: "sidebar-profile",        label: "Mein Profil" },
];

const STX_ROUTES = ["/stilltouchedx"];
function isSTXContext(pathname) {
  if (pathname.startsWith("/user/") && pathname.endsWith("/stx")) return true;
  return STX_ROUTES.some((r) => pathname.startsWith(r));
}

export default function DesktopSidebar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [watchUnread, setWatchUnread] = useState(0);
  const isStx = isSTXContext(pathname);

  useEffect(() => {
    if (!user || user === false) return;
    const load = async () => {
      try { const { data } = await api.get("/messages/unread"); setUnread(data.total || 0); } catch { /* ignore */ }
      try { const { data } = await api.get("/me/watch-notifications/summary"); setWatchUnread(data.total || 0); } catch { /* ignore */ }
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const isActive = (path) => {
    if (path === "/") return pathname === "/";
    return pathname.startsWith(path);
  };

  // iter119: STX-aware Farben. In STX-Kontext greifen die CSS-Variablen der Palette
  // (--stx-bg, --stx-border, --stx-text, --stx-panel), damit die linke Spalte
  // in Dark Mode komplett schwarz durchgezeichnet ist.
  const asideBg = isStx ? "bg-stx-bg border-stx-border" : "bg-white border-[#E5E5E5]";
  const textMuted = isStx ? "text-stx-text-muted" : "text-[#666]";
  const textActive = isStx ? "text-stx-text" : "text-[#0A0A0A]";
  const activeBg = isStx ? "bg-stx-panel" : "bg-[#F5F5F5]";
  const hoverBg = isStx ? "hover:bg-stx-panel-2" : "hover:bg-[#FAFAFA]";

  return (
    <aside
      data-testid="desktop-sidebar-left"
      data-stx={isStx ? "1" : "0"}
      className={`hidden 2xl:flex 2xl:sticky 2xl:top-14 2xl:h-[calc(100vh-3.5rem)] flex-col shrink-0 border-r ${asideBg} py-6 px-3 overflow-y-auto`}
    >
      <nav className="flex flex-col gap-1">
        {ITEMS.map((item) => {
          const active = isActive(item.path);
          const badge = item.unreadBadge ? unread : item.watchBadge ? watchUnread : 0;
          const Icon = item.icon;
          const label = t(`sidebar.${item.key}`, { defaultValue: item.label });
          return (
            <button
              key={item.key}
              data-testid={item.testId}
              onClick={() => navigate(item.path)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
                active
                  ? `${activeBg} ${textActive} font-semibold`
                  : `${textMuted} ${hoverBg} hover:${textActive}`
              }`}
            >
              <Icon size={22} strokeWidth={active ? 2.5 : 1.75} />
              <span className="text-sm">{label}</span>
              {badge > 0 && (
                <span className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {badge > 9 ? "9+" : badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
